import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import { getIconComponent } from "@/Utils/getIconComponent";
import FormLabel from "./FormLabel";
import axiosInstance from "@/Api/AxiosInstance";
import { LuSearch } from "react-icons/lu";

export default function InputTextarea({
  label,
  sublabel,
  actionText,
  onActionClick,
  placeholder,
  value = "",
  onChange,
  onBlur,
  type = "text",
  dataType,
  options,
  labelKey = "label",
  valueKey = "value",
  freeSolo = true,
  error = false,
  helperText = "",
  disabled,
  startIconName,
  endIconName,
  outerIconName,
  iconColor,
  sx = {},
  width,
  height,
  labelGap,
  required = false,
  important = false,
  onSelectOption,
  defaultValue,
  ...props
}) {
  const [isTouched, setIsTouched] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [internalValue, setInternalValue] = useState(""); // ✅ NEW: Internal state

  const StartIcon = startIconName
    ? getIconComponent(startIconName, {
        color: iconColor || "var(--gray-500)",
        size: 22,
      })
    : null;
  const EndIcon = endIconName
    ? getIconComponent(endIconName, {
        color: iconColor || "var(--gray-500)",
        size: 22,
      })
    : null;
  const OuterIcon = outerIconName
    ? getIconComponent(outerIconName, {
        color: iconColor || "var(--gray-500)",
        size: 22,
      })
    : null;

  const isEmpty = value === "" || value === null || value === undefined;
  const showError = error || (required && isTouched && isEmpty);

  // Normalize options
  //   const normalizedOptions = React.useMemo(() => {
  //     const sourceArray = Array.isArray(options) ? options : dropdownOptions;
  // console.log("sourceArray", sourceArray);
  //     return sourceArray.map((opt) =>
  //       typeof opt === "string"
  //         ? { label: opt, value: opt }
  //         : {
  //             label: opt?.[labelKey] ?? "",
  //             value: opt?.[valueKey] ?? opt,
  //             ...opt,
  //           }
  //     );
  //   }, [options, dropdownOptions, labelKey, valueKey]);

  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt) => {
      // normalize shape to { id, label, value, raw }
      const id = opt?.id ?? (opt?.value && opt.value.id) ?? opt?.value ?? opt;
      const value = opt?.value ?? opt?.id ?? id;
      const label =
        opt?.label ?? opt?.name ?? (typeof opt === "string" ? opt : "");
      const description = opt?.description ?? opt?.raw?.description ?? "";
      return { id, value, label, raw: opt, description };
    });
  }, [options]);

  const selectedOption =
    normalizedOptions.find((opt) =>
      typeof value === "object"
        ?( opt?.value?.id || opt?.value) === value?.id
        : opt?.value === value
    ) || null;

  // Extract value

  // ✅ EXTRACTED: Separate function for value extraction
  const getDisplayValue = (val) => {
    if (!val || val === "" || val === null || val === undefined) return "";

    // If value is an object, extract the display text
    if (typeof val === "object") {
      // Try different possible keys for the display text
      const displayValue = val.name || val.label || val.value || "";
      return displayValue;
    }

    // If value is a string/number, use it directly
    return String(val);
  };

  const jsonValue = getDisplayValue(value);

  useEffect(() => {
    const fetchOptions = async () => {
      if (options?.url) {
        try {
          setLoading(true);
          setFetchError("");
          const res = await axiosInstance.get(options.url);
          const data = res.data || [];
          setDropdownOptions(data);
        } catch (err) {
          setFetchError("Failed to load options");
          console.error("Error fetching options:", err);
        } finally {
          setLoading(false);
        }
      }
    };

    if (options?.url) {
      fetchOptions();
    }
  }, [options?.url]);

  const handleChange = (e) => {
    setIsTouched(true);
    let newValue = e?.target?.value ?? e;

    // numeric-only handling
    if ((type === "number" || dataType === "int") && props.maxLength <= 6) {
      newValue = newValue.replace(/[^0-9]/g, "");
    }

    // max length check
    if (props.maxLength && newValue.length > props.maxLength) {
      newValue = newValue.slice(0, props.maxLength);
    }

    if (type === "password") {
      newValue = newValue.replace(/\s/g, "");
    }

    if (onChange) {
      onChange({
        ...e,
        target: {
          ...e?.target,
          value: newValue,
        },
      });
    }
  };

  const handleBlur = (e) => {
    setIsTouched(true);
    if (onBlur) {
      onBlur(e);
    }
  };

  // Simple renderIcon function for TextArea
  const renderIcon = (iconName) => {
    return iconName
      ? getIconComponent(iconName, {
          color: iconColor || "var(--gray-400)",
          size: 20,
          fontWeight: "bold",
        })
      : null;
  };

  const renderAutocomplete = () => (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1}
      sx={{ width: width || "100%" }}
    >
      {StartIcon && <Box pt={"6px"}>{StartIcon}</Box>}

      <Autocomplete
        freeSolo={freeSolo}
        disablePortal
        options={selectedOption ? [selectedOption] : normalizedOptions}
        getOptionLabel={(option) => {
          if (typeof option?.label === "string" && option.label.trim() !== "") {
            return option.label;
          }
          if (option?.name?.name) {
            return option.name.name;
          }
          if (option?.value?.name) {
            return option.value.name;
          }
          return String(option?.name ?? option?.value ?? "");
        }}
        value={selectedOption}
        loading={loading}
        renderOption={(props, option) => {
          // extract only primitive (string/number/boolean) values
          const singleValueEntries = Object.entries(option || {})
            .filter(
              ([key, value]) =>
                key !== "label" &&
                key !== "name" &&
                key !== "id" &&
                (typeof value === "string" ||
                  typeof value === "number" ||
                  typeof value === "boolean")
            )
            .slice(0, 3); // show only first 3 primitive values

          return (
            <li {...props}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  px: 1,
                  py: 0.5,
                }}
              >
                {/* ✅ Always show label or name first */}
                <Typography sx={{ fontWeight: 600, color: "text.primary" }}>
                  {option?.label || option?.name || "Unnamed"}
                </Typography>

                {/* ✅ Show only primitive value fields */}
                {singleValueEntries.map(([key, value]) => (
                  <Typography
                    key={key}
                    sx={{
                      fontSize: 13,
                      color: "text.secondary",
                      textTransform: "capitalize",
                    }}
                  >
                    {`${key} : ${value}`}
                  </Typography>
                ))}
              </Box>
            </li>
          );
        }}
        onChange={(e, newValue) => {
          setIsTouched(true);
          if (!newValue) {
            onChange({ target: { value: "" } });
            return;
          }
          const finalVal =
            typeof newValue === "string" ? newValue : (newValue?.value ?? "");

          onChange({
            target: {
              value: finalVal,
              optionData: newValue,
            },
          });
        }}
        sx={{
          width: "100%",
          "& .MuiAutocomplete-popper": { zIndex: 9999 },
          "& .MuiAutocomplete-paper": {
            marginTop: "1px",
            border: "1px solid var(--gray-300)",
            borderRadius: "6px",
            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            disabled={disabled}
            size="small"
            placeholder={placeholder}
            onBlur={handleBlur}
            error={showError || Boolean(fetchError)}
            // helperText={fetchError || (showError ? helperText || `${label || 'Field'} is required` : "")}
            InputProps={{
              ...params.InputProps,
              startAdornment: startIconName && (
                <InputAdornment position="start">
                  {renderIcon(startIconName)}
                </InputAdornment>
              ),
              // if the data is fetching from API -> then show loading icon, otherwise show dropdown icon
              // if the value is selected then show clear icon
              endAdornment: (
                <>
                  <>
                    {!value && options?.length > 0 && (
                      <InputAdornment position="end">
                        {loading ? (
                          <CircularProgress
                            size={18}
                            color="var(--brand-900)"
                          />
                        ) : (
                          <Box
                            //  onClick={disabled ? undefined : iconOnClick}
                            sx={{
                              cursor: disabled ? "not-allowed" : "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            <LuSearch
                              color={iconColor || "var(--gray-400)"}
                              size={20}
                            />
                          </Box>
                        )}
                      </InputAdornment>
                    )}

                    {params.InputProps.endAdornment &&
                      React.cloneElement(params.InputProps.endAdornment, {
                        onClick: (e) => {
                          e.stopPropagation();
                          if (onSelectOption) onSelectOption(null);
                        },
                      })}
                  </>
                </>
              ),
              style: {
                height: height ? height : undefined,
                alignItems: "flex-start", // top-align wrapper
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                minWidth: width || 150,
                fontSize: "14px",
                backgroundColor: disabled ? "var(--gray-50)" : "var(--gray-0)",
                cursor: disabled ? "not-allowed" : "text",
                "& .MuiAutocomplete-endAdornment": {
                  alignItems: "flex-start", // make the whole endAdornment container top aligned
                  top: "8px", // ⬅️ adjust this to match your text baseline
                  transform: "none", // remove default translateY(-50%)
                },
                "& fieldset": {
                  border: showError
                    ? "1px solid var(--red-900) !important"
                    : "1px solid var(--gray-300)",
                  borderRadius: "6px",
                },
                "&:hover fieldset": {
                  borderColor: showError ? "var(--red-900)" : "var(--gray-300)",
                },
                "&.Mui-focused fieldset": {
                  border: "1px solid",
                  borderColor: showError
                    ? "var(--red-900)"
                    : "var(--brand-600) !important",
                  boxShadow: showError ? "none" : "0 0 0 3px var(--brand-200)",
                },
                "& textarea": {
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  lineHeight: "1.5",
                  "&::placeholder": {
                    opacity: 1,
                    color: "var(--gray-500)",
                  },
                },
              },
              "& .MuiFormHelperText-root": {
                color: showError ? "var(--red-900) !important" : "inherit",
                fontWeight: 400,
                marginLeft: 0,
              },
            }}
          />
        )}
      />

      {EndIcon && <Box pt={"6px"}>{EndIcon}</Box>}
      {OuterIcon && <Box pt={"6px"}>{OuterIcon}</Box>}
    </Box>
  );

  const renderNormalTextarea = () => (
    <Box
      display="flex"
      alignItems="flex-start"
      gap={1}
      sx={{ width: width || "100%" }}
    >
      {StartIcon && <Box pt={"6px"}>{StartIcon}</Box>}
      <TextareaAutosize
        key={label}
        placeholder={placeholder}
        value={jsonValue}
        disabled={disabled}
        defaultValue={defaultValue}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{
          display: "block",
          border: showError
            ? "1px solid red"
            : isTouched
              ? "1px solid var(--brand-600)"
              : "1px solid var(--gray-300)",
          boxShadow:
            isTouched && !showError ? "0 0 0 4px var(--brand-200)" : "none",
          cursor: disabled ? "not-allowed" : "text",
          backgroundColor: disabled ? "var(--gray-50)" : "var(--gray-0)",
          resize: "none",
          fontFamily: "inherit",
          fontSize: "14px",
          padding: "8px",
          width: width || "100%",
          height: height ? height : "auto",
          outline: "none",
          borderRadius: 6,
          boxSizing: "border-box", // <-- add this
          verticalAlign: "top",
          textAlign: "left", // <-- add this
          ...sx,
        }}
        {...props}
      />
      {EndIcon && <Box pt={"6px"}>{EndIcon}</Box>}
      {OuterIcon && <Box pt={"6px"}>{OuterIcon}</Box>}
    </Box>
  );

  const hasOptions =
    (Array.isArray(options) && options.length > 0) || options?.url;

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={actionText || label ? 1 : 0}
      sx={{ width: width || "100%" }}
    >
      {/* Label & Action */}
      {(label || actionText) && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ ...(label && { paddingRight: 5 }) }}
        >
          <FormLabel label={actionText} gap={labelGap} required={required} />
          {label && (
            <Typography
              variant="bodyMedium"
              onClick={!disabled ? onActionClick : undefined}
              sx={{
                color: disabled ? "var(--gray-400)" : "var(--link-blue)",
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              {label}
            </Typography>
          )}
        </Box>
      )}
      {/* Render appropriate component */}
      {(Array.isArray(options) && options.length > 0) ||
      (options && options.url)
        ? renderAutocomplete()
        : renderNormalTextarea()}

      {/* Helper text */}
      {(showError || fetchError) && (
        <FormHelperText sx={{ color: "var(--red-900) !important" }}>
          {fetchError || helperText || "This field is required"}
        </FormHelperText>
      )}
    </Box>
  );
}
